import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useUpdateMyDataMutation } from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { User } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import PhoneInput from "@/components/PhoneInput";
import { getDefaultCountryByBrowserLanguage } from "@/constants/countries";
import { formatPhoneForAPI } from "@/lib/format/phone";

interface ProfileCompletionModalProps {
  open: boolean;
  missingFields: {
    name: boolean;
    phoneNumber: boolean;
  };
  currentName?: string | null;
  onComplete: () => void;
}

export const ProfileCompletionModal = ({ open, missingFields, currentName, onComplete }: ProfileCompletionModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [updateMyData] = useUpdateMyDataMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(getDefaultCountryByBrowserLanguage());

  const isFormValid =
    (!missingFields.name || name.trim()) &&
    (!missingFields.phoneNumber || phoneNumber.replace(/\D/g, '').length >= 4);

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);
    try {
      const finalName = missingFields.name ? name.trim() : (currentName || name.trim());

      await updateMyData({
        variables: {
          name: finalName,
          ...(missingFields.phoneNumber && { phoneNumber: formatPhoneForAPI(phoneNumber, phoneCountry) }),
        },
      });

      await onComplete();

      toast({
        title: t('profileCompletion.toast.success'),
        description: t('profileCompletion.toast.profileUpdated'),
      });
    } catch (error) {
      console.error("Failed to complete profile:", error);
      toast({
        title: t('profileCompletion.toast.error'),
        description: t('profileCompletion.toast.couldNotUpdate'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} modal={true}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        <DialogHeader className="space-y-3 px-6 pt-6 shrink-0">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <User className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-2xl">
            {t('profileCompletion.title')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('profileCompletion.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {/* Name field */}
            {missingFields.name && (
              <div className="space-y-2">
                <Label htmlFor="user-name" className="text-base font-medium">
                  {t('profileCompletion.name')} *
                </Label>
                <Input
                  id="user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('profileCompletion.namePlaceholder')}
                />
              </div>
            )}

            {/* Phone number field */}
            {missingFields.phoneNumber && (
              <div className="space-y-2">
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  country={phoneCountry}
                  onCountryChange={setPhoneCountry}
                  label={`${t('profileCompletion.phoneNumber')} *`}
                  required
                />
              </div>
            )}

          </div>
        </ScrollArea>

        <div className="px-6 pb-6 pt-2 shrink-0">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? t('profileCompletion.saving') : t('profileCompletion.continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
